import dayjs from 'dayjs';
import { JobType, MeterRateTaskType } from '../../redux/jobs/jobs.types';
import { METER_TYPES, resolveMeterType, resolveUnitByObis, revolveMeterTypeByObis } from '../../helper/MeterHelper';
import { getLatestReadingEvent, getLatestReadingResult } from '../../helper/JobHelper';

function joinParts(separator: string, ...parts: Array<string | undefined>): string {
    return parts.filter(part => part != null && part.trim() !== '').join(separator);
}

const isKnownMeterType = (value: unknown): value is METER_TYPES => Object.values(METER_TYPES).includes(value as METER_TYPES);

// Row-level meter-type resolution shared by the Excel export builders
// (ExcelWorkbookBuilder.ts, LegacyShareExportBuilder.ts): explicit meter type
// first, then a custom meter type, then a fallback derived from the task's OBIS.
export const resolveRowMeterType = (job: JobType, task: MeterRateTaskType): METER_TYPES => {
    if (isKnownMeterType(job.meter.meterType)) {
        return job.meter.meterType;
    }

    if (isKnownMeterType(job.meter.customMeterType)) {
        return job.meter.customMeterType;
    }

    return revolveMeterTypeByObis(task.obis);
};

export type ExportColumnValue = string | number | Date | '';

// Excel treats a leading =, +, -, or @ as the start of a formula. Column values
// here come from customer/partner master data (name, address, comments, ...),
// so an unescaped leading character could inject a formula into the exported
// file. Prefixing with a single quote forces Excel to treat the cell as text.
export function sanitizeCellText(value: string): string {
    return /^[=+\-@]/.test(value) ? `'${value}` : value;
}

export function extractExportColumnValue(key: string, job: JobType, task?: MeterRateTaskType): ExportColumnValue {
    const raw = extractRawExportColumnValue(key, job, task);
    return typeof raw === 'string' ? sanitizeCellText(raw) : raw;
}

function extractRawExportColumnValue(key: string, job: JobType, task?: MeterRateTaskType): ExportColumnValue {
    const meterAddress = job.meter.meterAddress;
    const customerMailingAddress = job.customer.mailingAddress;
    const partnerMailingAddress = job.partner?.mailingAddress;

    switch (key) {
        case 'meter.address.street':
            return joinParts(' ', meterAddress?.street, meterAddress?.houseNumber);
        case 'meter.address.zip':
            return meterAddress?.zip || '';
        case 'meter.address.city':
            return joinParts(' / ', meterAddress?.city, meterAddress?.cityDistrict);
        case 'meter.address.objectKey':
            return meterAddress?.objectKey || '';
        case 'meter.address.locationDescription':
            return meterAddress?.locationDescription || '';
        case 'meter.address.locationHint':
            return meterAddress?.hint || '';
        case 'customer.customerNumber':
            return job.customer.customerNumber || '';
        case 'customer.name':
            return joinParts(' ', job.customer.lastname, job.customer.firstname);
        case 'customer.company':
            return job.customer.company || '';
        case 'customer.postalAddress':
            if (!customerMailingAddress) return '';
            return [
                customerMailingAddress.recipient,
                joinParts(' ', customerMailingAddress.street, customerMailingAddress.houseNumber),
                joinParts(' ', customerMailingAddress.zip, customerMailingAddress.city),
            ].filter(Boolean).join(', ');
        case 'customer.mailingAddress.street':
            return joinParts(' ', customerMailingAddress?.street, customerMailingAddress?.houseNumber);
        case 'customer.mailingAddress.zip':
            return customerMailingAddress?.zip || '';
        case 'customer.mailingAddress.city':
            return joinParts(' / ', customerMailingAddress?.city, customerMailingAddress?.cityDistrict);
        case 'customer.mailingAddress.postbox':
            return customerMailingAddress?.postbox || '';
        case 'customer.contact.email':
            return job.customer.contactInformation?.email || '';
        case 'customer.contact.phone':
            return job.customer.contactInformation?.phone || '';
        case 'customer.contact.mobile':
            return job.customer.contactInformation?.mobile || '';
        case 'partner.partnerKey':
            return job.partner?.identifier || '';
        case 'partner.name':
            return joinParts(' ', job.partner?.lastname, job.partner?.firstname);
        case 'partner.partnerCompany':
            return job.partner?.company || '';
        case 'partner.contractId':
            return job.partner?.contractId || '';
        case 'partner.mailingAddress.street':
            return joinParts(' ', partnerMailingAddress?.street, partnerMailingAddress?.houseNumber);
        case 'partner.mailingAddress.zip':
            return partnerMailingAddress?.zip || '';
        case 'partner.mailingAddress.city':
            return joinParts(' / ', partnerMailingAddress?.city, partnerMailingAddress?.cityDistrict);
        case 'partner.mailingAddress.postbox':
            return partnerMailingAddress?.postbox || '';
        case 'partner.contact.email':
            return job.partner?.contactInformation?.email || '';
        case 'partner.contact.phone':
            return job.partner?.contactInformation?.phone || '';
        case 'partner.contact.mobile':
            return job.partner?.contactInformation?.mobile || '';
        case 'meterRateTask.tariffName':
            return task?.rateName || '';
        case 'meterRateTask.unit':
            return task?.unit || resolveUnitByObis(task?.obis) || '';
        case 'meterRateTask.meterType': {
            const rawType = job.meter.customMeterType || resolveMeterType(job.meter) || job.meter.meterType || '';
            return rawType || '';
        }
        case 'meterRateTask.maloId':
            return job.meter.maLoId || '';
        case 'meterRateTask.meloId':
            return job.meter.meLoId || '';
        case 'job.externalIdentifier':
            return job.externalIdentifier || '';
        case 'meterRateTask.validUntil':
            return job.validUntil ? dayjs(job.validUntil).toDate() : '';
        case 'meterRateTask.executionDeadline':
            return job.jobConstraints?.latestExecution ? dayjs(job.jobConstraints.latestExecution).toDate() : '';
        case 'meterRateTask.lastReadingValue':
            return getLatestReadingEvent(task)?.readingValue ?? '';
        case 'meterRateTask.lastReadingDate': {
            const readingDate = getLatestReadingEvent(task)?.readingDate;
            return readingDate ? dayjs(readingDate).toDate() : '';
        }
        case 'meterRateTask.lastResultValue':
            return getLatestReadingResult(task)?.readingValue ?? '';
        case 'meterRateTask.lastResultDate': {
            const readingDate = getLatestReadingResult(task)?.readingDate;
            return readingDate ? dayjs(readingDate).toDate() : '';
        }
        default:
            return '';
    }
}

export function hasExportColumnValue(key: string, jobs: JobType[]): boolean {
    return jobs.some(job => {
        if (!job?.meter || !job.customer) {
            return false;
        }

        const tasks = job.meter.meterRateTasks?.length > 0 ? job.meter.meterRateTasks : [undefined];
        return tasks.some(task => {
            if (key === 'meterRateTask.meterType') {
                return String(job.meter.customMeterType || job.meter.meterType || '').trim() !== '';
            }
            if (key === 'meterRateTask.unit') {
                return String(task?.unit || '').trim() !== '';
            }
            const value = extractExportColumnValue(key, job, task);
            return value instanceof Date || typeof value === 'number' || String(value).trim() !== '';
        });
    });
}
